import { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/system';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GroupIcon from '@mui/icons-material/Group';
import { Controller, useWatch, type Control, type UseFormSetValue } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useDropzone } from 'react-dropzone';
import { PRIORITY_ID } from 'Constants/priority';
import { priority } from 'Constants/Database';
import { colors } from 'Constants/Colors';
import bytesToMegabytes from 'Utils/BytesToMegaBytes';
import useTranslation from 'Hooks/useTranslation';
import SelectIcon from '../SelectIcon';
import UserAutocomplete from 'Pages/EditCourseSessions/Components/UserAutocomplete';
import type { BulkApproveFormValues } from './schema';
import useInstructorOptions, { type InstructorOption } from './useInstructorOptions';

/** Empleado tal como lo expone UserAutocomplete (lista de empleados). */
interface EmployeeOption {
  id: string;
  fullName: string;
}

interface Props {
  control: Control<BulkApproveFormValues>;
  setValue: UseFormSetValue<BulkApproveFormValues>;
  priorityId: string;
  expanded: boolean;
  toggleExpanded: () => void;
  eligibleCount: number;
}

const priorityIdToLegacyType = (priorityId: string): string => {
  if (priorityId === PRIORITY_ID.normativa) return priority.Normativa;
  if (priorityId === PRIORITY_ID.institucional) return priority.Institucional;
  return priority.Tecnica;
};

const headerIconBoxSx: SxProps<Theme> = {
  width: 60,
  height: 60,
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: colors.GREEN_ULTRA_LIGHT,
  flex: '0 0 auto',
};

const evidenceColumnSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const messageSx: SxProps<Theme> = {
  color: colors.GRAY_DARKER,
  fontSize: 14,
  lineHeight: 1.5,
};

const titleSx: SxProps<Theme> = {
  fontWeight: 'bold',
  fontSize: 18,
  color: colors.BLACK,
};

const dropzoneAreaSx = {
  bgcolor: colors.GREEN_ULTRA_LIGHT,
  borderRadius: '6px',
  padding: 3,
  textAlign: 'center' as const,
  cursor: 'pointer',
  border: '2px dashed transparent',
  transition: 'border-color 120ms',
  '&:hover': { borderColor: colors.GREEN },
};

const toggleButtonSx: SxProps<Theme> = {
  color: colors.GREEN,
  borderColor: colors.GREEN,
  textTransform: 'none',
  '&:hover': { borderColor: colors.GREEN_LEAF, bgcolor: colors.GREEN_ULTRA_LIGHT },
};

const EvidenceSection = ({
  control, setValue, priorityId, expanded, toggleExpanded, eligibleCount,
}: Props) => {
  const { t2 } = useTranslation('EmployeeAssess');

  const isNormativa = priorityId === PRIORITY_ID.normativa;
  const isTecnica = priorityId === PRIORITY_ID.tecnica;
  const isInstitucional = priorityId === PRIORITY_ID.institucional;
  const legacyType = priorityIdToLegacyType(priorityId);

  // El instructor solo aplica cuando hay aprobación manual (todo excepto Normativa).
  const { options: instructorOptions, loading: instructorsLoading } = useInstructorOptions(!isNormativa);

  // Origen del instructor: catálogo (externo) o empleado (interno).
  const instructorType = useWatch({ control, name: 'instructorType' }) ?? 'external';
  // El empleado elegido se guarda local para que UserAutocomplete lo muestre;
  // al form solo viaja su id.
  const [internalEmployee, setInternalEmployee] = useState<EmployeeOption | null>(null);

  const handleInstructorTypeChange = (_event: unknown, next: string | null) => {
    if (!next || next === instructorType) return;
    setValue('instructorType', next as 'external' | 'internal');
    setValue('instructorId', null);
    setValue('instructorUserId', null);
    setInternalEmployee(null);
  };

  const message = isNormativa
    ? t2('NormativeMessage')
    : isInstitucional
    ? t2('InstitutionalMessage')
    : t2('TechnicalMessage');

  return (
    <Box sx={evidenceColumnSx}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={headerIconBoxSx}>
          <SelectIcon priorityType={legacyType} />
        </Box>
        <Typography sx={titleSx}>{t2('AssessCourse')}</Typography>
      </Stack>

      <Typography sx={messageSx}>{message}</Typography>

      {isTecnica && (
        <Controller
          name="file"
          control={control}
          render={({ field, fieldState }) => (
            <DropzoneInput
              file={field.value}
              onChange={f => field.onChange(f)}
              error={fieldState.error?.message}
            />
          )}
        />
      )}

      {!isNormativa && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: 14, color: colors.BLACK }}>
            {t2('instructor_label')}
          </Typography>
          <ToggleButtonGroup
            value={instructorType}
            exclusive
            onChange={handleInstructorTypeChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root.Mui-selected': {
                bgcolor: colors.GREEN,
                color: colors.WHITE,
                '&:hover': { bgcolor: colors.GREEN_LEAF },
              },
            }}
          >
            <ToggleButton value="external" sx={{ textTransform: 'none' }}>
              {t2('instructor_type_external')}
            </ToggleButton>
            <ToggleButton value="internal" sx={{ textTransform: 'none' }}>
              {t2('instructor_type_internal')}
            </ToggleButton>
          </ToggleButtonGroup>

          {instructorType === 'external' ? (
            <Controller
              name="instructorId"
              control={control}
              render={({ field }) => {
                const selected = instructorOptions.find(option => option.id === field.value) ?? null;
                return (
                  <Autocomplete<InstructorOption, false, false, false>
                    options={instructorOptions}
                    loading={instructorsLoading}
                    getOptionLabel={option => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={selected}
                    onChange={(_event, option) => field.onChange(option ? option.id : null)}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={t2('instructor_placeholder')}
                        size="small"
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: colors.GREEN,
                          },
                          '& .MuiInputLabel-root.Mui-focused': { color: colors.GREEN },
                        }}
                      />
                    )}
                  />
                );
              }}
            />
          ) : (
            <Controller
              name="instructorUserId"
              control={control}
              render={({ field }) => (
                <UserAutocomplete
                  value={internalEmployee}
                  onChange={(employee: EmployeeOption | null) => {
                    setInternalEmployee(employee);
                    field.onChange(employee?.id ?? null);
                  }}
                  placeholder={t2('instructor_internal_placeholder')}
                  noOptionsText={t2('instructor_internal_empty')}
                />
              )}
            />
          )}
        </Box>
      )}

      {!isNormativa && (
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Controller
                name="evaluationDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label={t2('evaluation_date')}
                    value={field.value}
                    onChange={date => {
                      field.onChange(date);
                      setValue('evaluationDateTouched', true);
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: colors.GREEN,
                          },
                          '& .MuiInputLabel-root.Mui-focused': { color: colors.GREEN },
                        }}
                      />
                    )}
                  />
                )}
              />
            </LocalizationProvider>
          </Box>
          <Button
            variant="outlined"
            startIcon={<GroupIcon />}
            onClick={toggleExpanded}
            disabled={eligibleCount === 0}
            size="small"
            sx={{ ...toggleButtonSx, flex: '0 0 auto', whiteSpace: 'nowrap', height: 40 }}
          >
            {expanded
              ? t2('hide_multi_select')
              : `${t2('select_multiple_employees')} (${eligibleCount})`}
          </Button>
        </Stack>
      )}

    </Box>
  );
};

interface DropzoneInputProps {
  file: File | null;
  onChange: (f: File | null) => void;
  error?: string;
}

const DropzoneInput = ({ file, onChange, error }: DropzoneInputProps) => {
  const { t2 } = useTranslation('EmployeeAssess');
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ['.docx', 'application/pdf', '.doc', '.mov', '.mp4'],
    maxFiles: 1,
    onDrop: accepted => onChange(accepted[0] ?? null),
  });

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={[
          dropzoneAreaSx,
          isDragActive ? { borderColor: colors.GREEN } : {},
        ]}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: 48, color: colors.GREEN }} />
        <Typography variant="body2" sx={{ color: colors.BLACK, mt: 1 }}>
          {t2('UploadFiles')}
        </Typography>
        <Typography variant="caption" sx={{ color: colors.GRAY_DARKER }}>
          {t2('TypeFiles')}
        </Typography>
      </Box>
      {file && (
        <Typography sx={{ mt: 1, fontSize: 12, textAlign: 'center', color: colors.BLACK }}>
          {file.name} — {bytesToMegabytes(file.size)} MB
        </Typography>
      )}
      {error && (
        <Typography variant="caption" sx={{ color: colors.RED, mt: 1, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default EvidenceSection;
